import { createField } from '@/components/forms/field.schema';
import { SignatureField } from '@/components/forms/fields/SignatureField';
import {
  Briefcase,
  Building2,
  Calendar,
  Car,
  CheckCircle,
  CircleDot,
  FileText,
  Hash,
  Lock,
  Mail,
  MapPin,
  PenLine,
  Phone,
  RotateCcw,
  User,
  Wallet,
} from 'lucide-react';
import {
  WORKORDER_PROGRESS_OPTIONS,
  SETTLEMENT_LABELS,
  Settlement,
} from '@certhon/models';
import { z } from 'zod';

export const workorderFields = {
  // Identity
  recnum: createField(z.number(), {
    fieldType: 'text',
    label: 'Recnum',
    readOnly: true,
    icon: Hash,
  }),
  id: createField(z.number(), {
    fieldType: 'text',
    label: 'Workorder No.',
    readOnly: true,
    icon: Hash,
  }),

  // Customer info section
  customer_name: createField(z.string().nullable(), {
    fieldType: 'text',
    label: 'Customer',
    readOnly: true,
    icon: Building2,
  }),
  werk_id: createField(z.number(), {
    fieldType: 'foreignKey',
    entity: 'work',
    label: 'Work',
    readOnly: true,
    icon: Briefcase,
  }),
  customer_address: createField(z.string().nullable(), {
    fieldType: 'text',
    label: 'Address',
    readOnly: true,
    icon: MapPin,
  }),
  customer_phone: createField(z.string().nullable(), {
    fieldType: 'text',
    label: 'Phone',
    readOnly: true,
    icon: Phone,
  }),
  customer_email: createField(z.string().nullable(), {
    fieldType: 'text',
    label: 'Email',
    readOnly: true,
    icon: Mail,
  }),

  // Planning
  execution_date: createField(z.date().nullable(), {
    fieldType: 'date',
    label: 'Start date',
    readOnly: true,
    icon: Calendar,
  }),
  end_date: createField(z.date().nullable(), {
    fieldType: 'date',
    label: 'End date',
    readOnly: true,
    icon: Calendar,
  }),
  progress: createField(
    z.enum(
      WORKORDER_PROGRESS_OPTIONS.map((o) => o.value) as [string, ...string[]],
    ),
    {
      fieldType: 'select',
      label: 'Status',
      options: [...WORKORDER_PROGRESS_OPTIONS],
      readOnly: true,
      icon: CircleDot,
    },
  ),

  // Location section
  location_address: createField(z.string().nullable(), {
    fieldType: 'text',
    label: 'Location',
    readOnly: true,
    icon: MapPin,
  }),
  location_doorcode: createField(z.string().nullable(), {
    fieldType: 'text',
    label: 'Door Code',
    readOnly: true,
    icon: Lock,
  }),
  location_phone: createField(z.string().nullable(), {
    fieldType: 'text',
    label: 'Location Phone',
    readOnly: true,
    icon: Phone,
  }),

  // Contact section
  contact_name: createField(z.string().nullable(), {
    fieldType: 'text',
    label: 'Contact Person',
    readOnly: true,
    icon: User,
  }),
  contact_phone: createField(z.string().nullable(), {
    fieldType: 'text',
    label: 'Contact Phone',
    readOnly: true,
    icon: Phone,
  }),

  // Assignment
  assigned_user_id: createField(z.number().nullable(), {
    fieldType: 'foreignKey',
    entity: 'user',
    label: 'Technician',
    readOnly: true,
    icon: User,
  }),
  creator_name: createField(z.string().nullable(), {
    fieldType: 'text',
    label: 'Creator',
    readOnly: true,
    icon: User,
  }),
  department: createField(z.string().nullable(), {
    fieldType: 'text',
    label: 'Department',
    readOnly: true,
    icon: Building2,
  }),
  settlement: createField(
    z
      .enum([
        'cost_plus',
        'contracted',
        'quoted',
        'final_account',
        'archived',
        'invoiced',
        'warranty',
      ])
      .nullable(),
    {
      fieldType: 'select',
      label: 'Settlement',
      options: Object.entries(SETTLEMENT_LABELS).map(([value, label]) => ({
        value: value as Settlement,
        label,
      })),
      readOnly: true,
      icon: Wallet,
    },
  ),

  // Workorder details
  description: createField(z.string().nullable(), {
    fieldType: 'textarea',
    label: 'Description',
    icon: FileText,
    fullWidth: true,
    readOnly: true,
  }),

  // Work completed (editable)
  report: createField(z.string().nullable(), {
    fieldType: 'textarea',
    label: 'Work Completed',
    fullWidth: true,
  }),
  internal_note: createField(z.string().nullable(), {
    fieldType: 'textarea',
    label: 'Internal Note',
    fullWidth: true,
  }),

  // Status flags (editable)
  travelfee: createField(z.boolean(), {
    fieldType: 'boolean',
    label: 'Travel Fee',
    icon: Car,
  }),
  followup: createField(z.boolean(), {
    fieldType: 'boolean',
    label: 'Follow-up Required',
    icon: RotateCcw,
  }),
  completed: createField(z.boolean(), {
    fieldType: 'boolean',
    label: 'Completed',
    readOnly: true,
    icon: CheckCircle,
  }),
  signed: createField(z.boolean(), {
    fieldType: 'boolean',
    label: 'Signed',
    readOnly: true,
    icon: PenLine,
  }),
  date_completed: createField(z.date().nullable(), {
    fieldType: 'date',
    label: 'Date Completed',
    readOnly: true,
    icon: Calendar,
  }),

  // Signature
  email: createField(z.string().nullable(), {
    fieldType: 'text',
    label: 'Email for Report',
    icon: Mail,
  }),
  signature_data: createField(z.string().nullable(), {
    fieldType: 'custom',
    label: 'Signature',
    component: SignatureField,
    fullWidth: true,
  }),
};
